import { useEffect, useRef, useState } from "react";
import { api } from "../api/http.js";
import { onWs } from "../api/ws.js";
import { serverNow } from "../state/serverTime.js";
import { useSession } from "../state/session.js";
import { useBase } from "../base/state.js";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { Icon } from "../ui/Icon.js";
import { GameIcon } from "../assets/gameIcons.js";
import { useSocial } from "./state.js";
import { announcementFeed } from "./announcements.js";
import type { ChatMessage } from "./types.js";
import "./ticker.css";

/** Bounded real system messages; dismissed for this login, with no effect on chat unread state. */
export function GlobalTicker({ route, onVisibilityChange }: { route: string; onVisibilityChange(visible: boolean): void }) {
  const t = useT(), enabled = useSession(state => state.settings?.announcements !== false);
  const accountId = useSession(state => state.account?.id), recovery = useSession(state => state.recoveryVersion);
  const mode = useBase(state => state.mode.kind), panel = useBase(state => state.panel.kind);
  const [messages, setMessages] = useState<ChatMessage[]>([]), [index, setIndex] = useState(0), [obstructed, setObstructed] = useState(false);
  const excluded = useRef(new Set<string>());
  useEffect(() => {
    if (!accountId || !enabled) { setMessages([]); return; }
    let alive = true, refreshing = false, sequence = 0;
    const arrivals = new Map<string, { sequence: number; message: ChatMessage }>();
    const merge = (incoming: ChatMessage[]) => { if (alive) setMessages(current => announcementFeed([...current, ...incoming], serverNow(), excluded.current)); };
    const offMessage = onWs("chat.message", event => {
      const message = event.payload as ChatMessage;
      if (!announcementFeed([message], serverNow(), excluded.current).length) return;
      arrivals.set(message.id, { sequence: ++sequence, message });
      const retained = new Set(announcementFeed([...arrivals.values()].map(row => row.message), serverNow(), excluded.current).map(row => row.id));
      for (const id of arrivals.keys()) if (!retained.has(id)) arrivals.delete(id);
      merge([message]);
    });
    const offDelete = onWs("chat.deleted", event => {
      const deleted = event.payload as { id: string; channel: string };
      if (deleted.channel !== "system") return;
      excluded.current.add(deleted.id); arrivals.delete(deleted.id); merge([]);
    });
    const refresh = async () => {
      // Coalesce poll/resume triggers so a later caller cannot give an earlier shared GET
      // a newer boundary and thereby discard a push received while that request was pending.
      if (!alive || refreshing) return;
      refreshing = true;
      const startedAtSequence = sequence;
      try {
        const data = await api<{ messages: ChatMessage[] }>("GET", "/api/chat/announcements");
        if (!alive) return;
        const duringRequest = [...arrivals.values()].filter(row => row.sequence > startedAtSequence).map(row => row.message);
        setMessages(announcementFeed([...data.messages, ...duringRequest], serverNow(), excluded.current));
        for (const [id, row] of arrivals) if (row.sequence <= startedAtSequence) arrivals.delete(id);
      } catch { /* The current, expiring feed stays visible during a temporary read failure. */ }
      finally { refreshing = false; }
    };
    void refresh();
    const poll = setInterval(refresh, 60_000), expire = setInterval(() => merge([]), 10_000);
    const resume = () => { if (document.visibilityState === "visible") void refresh(); };
    document.addEventListener("visibilitychange", resume);
    return () => { alive = false; offMessage(); offDelete(); clearInterval(poll); clearInterval(expire); document.removeEventListener("visibilitychange", resume); };
  }, [accountId, enabled, recovery]);
  useEffect(() => {
    const check = () => setObstructed(!!document.querySelector('dialog[open], .opening-panel, [data-testid="terrace-status"], [data-testid="march-detail"], [data-testid="target-panel"]'));
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["open"] });
    check(); return () => observer.disconnect();
  }, []);
  const visible = enabled && !obstructed && !route.startsWith("/app/battle/") && !route.startsWith("/app/chat") && (route !== "/app" || mode === "view" && panel === "none");
  useEffect(() => { onVisibilityChange(visible); return () => onVisibilityChange(false); }, [visible, onVisibilityChange]);
  if (!visible) return null;
  const message = messages[index % messages.length];
  return <aside className="global-ticker" data-testid="global-ticker" aria-label={t("social.ticker.label")}>
    <button className="ticker-message" onClick={() => { useSocial.setState({ channel: message ? "system" : "world" }); navigate("/app/chat"); }} title={message?.text} aria-label={message ? `${t("social.ticker.open")}: ${message.text}` : pick({de:"Weltchat öffnen",en:"Open world chat"})}>
      {message?.card?.kind === "unit" ? <GameIcon id={message.card.unitId} size={34} /> : <Icon name="globe" size={24} />}
      <span><small>{message ? t("social.ticker.label") : pick({de:"KOMMUNIKATION",en:"COMMUNICATIONS"})}</small><strong>{message?.text ?? pick({de:"Weltchat öffnen",en:"Open world chat"})}</strong></span>
    </button>
    {messages.length > 1 ? <button className="ticker-next" onClick={() => setIndex(n => (n + 1) % messages.length)} aria-label={t("social.ticker.next")}><span>{index % messages.length + 1}/{messages.length}</span><Icon name="chevronRight" /></button> : null}
    <button className="comm-shortcut" onClick={()=>navigate('/app/reports')} data-testid="quick-reports">{pick({de:"Berichte",en:"Reports"})}</button>
    <button className="comm-shortcut" onClick={()=>navigate('/app/operations')} data-testid="quick-operations">{pick({de:"Einsätze",en:"Operations"})}</button>
    {message ? <button className="ticker-close" onClick={() => { excluded.current.add(message.id); setMessages(current => announcementFeed(current, serverNow(), excluded.current)); setIndex(0); }} aria-label={t("social.ticker.close")}><Icon name="close" /></button> : null}
  </aside>;
}
